import { useQuery } from "@apollo/client";
import { GET_SHAREABLE_LINK_BY_TOKEN } from "../gql";
import type { GetAssetByShareableLinkConnection } from "@casey/db-types";

interface GetShareableLinkByTokenResponse {
  getAssetByShareableLink: GetAssetByShareableLinkConnection;
}

interface GetShareableLinkByTokenVariables {
  token: string;
}

export function useShareableLinkByToken(
  token: string,
  options?: { skip?: boolean },
) {
  const { data, loading, error, refetch } = useQuery<
    GetShareableLinkByTokenResponse,
    GetShareableLinkByTokenVariables
  >(GET_SHAREABLE_LINK_BY_TOKEN, {
    variables: { token },
    skip: !token || options?.skip,
  });

  const record = data?.getAssetByShareableLink?.nodes?.[0];

  // Reconstruct shareableLink and asset objects from flattened data
  const shareableLink = record
    ? {
        token: record.token,
        assetId: record.assetId,
        organizationId: record.organizationId,
        accessType: record.accessType,
        targetUserId: record.targetUserId,
        expiresAt: record.expiresAt,
        viewCount: record.viewCount,
        lastAccessedAt: record.lastAccessedAt,
        createdAt: record.linkCreatedAt,
        createdByUserId: record.linkCreatedByUserId,
      }
    : null;

  const asset = record
    ? {
        id: record.assetId,
        title: record.assetTitle,
        content: record.assetContent,
        contentFileUrl: record.assetContentFileUrl,
        assetType: record.assetType,
        status: record.assetStatus,
        generationTemplate: record.assetGenerationTemplate,
        currentVersionNumber: record.assetCurrentVersionNumber,
        createdAt: record.assetCreatedAt,
        conversationWithProfiles: record.conversationId
          ? {
              id: record.conversationId,
              status: record.sessionStatus,
              participantProfileId: record.participantProfileId,
              participantProfileEmail: record.participantProfileEmail,
              participantProfileFirstName: record.participantProfileFirstName,
              participantProfileLastName: record.participantProfileLastName,
              participantProfileOrganizationName:
                record.participantProfileOrganizationName,
              participantProfileOrganizationDomain:
                record.participantProfileOrganizationDomain,
            }
          : null,
        project: record.projectId
          ? {
              id: record.projectId,
              name: record.projectName,
            }
          : null,
      }
    : null;

  return {
    shareableLink,
    asset,
    loading,
    error,
    refetch,
  };
}
